import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, FileText } from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { StoreHeader } from "../components/StoreHeader";
import { PriceFlash } from "../components/PriceFlash";
import { MetalTicker } from "../components/MetalTicker";
import { MonthlyBulletinButton } from "../components/MonthlyBulletinButton";
import { PhaseRail, PhaseIcon } from "../components/PhaseRail";
import { useCart } from "../context/CartContext";
import { formatINR, resolveImg } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEGACY = ["tmt", "cement", "structural", "binding_wire", "sheets"];

export default function Shop() {
  const { add } = useCart();
  const [cats, setCats] = useState([]);
  const [phases, setPhases] = useState([]);
  const [phase, setPhase] = useState(null);
  const [active, setActive] = useState("all");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dia, setDia] = useState(null);
  const [cemGrade, setCemGrade] = useState(null);

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCats(r.data)).catch(() => {});
    axios.get(`${API}/phases`).then((r) => setPhases(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setLoading(true);
    setDia(null); setCemGrade(null);
    const url = active !== "all" ? `${API}/products?category=${active}`
      : phase ? `${API}/products?phase=${phase}` : `${API}/products`;
    axios.get(url).then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [active, phase]);

  const selectPhase = (ph) => { setPhase(ph); setActive("all"); };
  const phaseCats = phase === null ? cats.filter((c) => LEGACY.includes(c.key)) : cats.filter((c) => c.phase === phase);
  const chips = phase !== null && phaseCats.length < 2 ? [] : phaseCats;
  const current = phases.find((p) => p.phase === phase);
  const totalSkus = phases.reduce((s, p) => s + (p.product_count || 0), 0);

  const quickAdd = (p) => { add(p, p.moq || 1); toast.success(`${p.name} added to cart`); };
  const rfqLink = (p) => `/request-quote?material=${encodeURIComponent(p.name)}&unit=${encodeURIComponent(p.uom || "")}&grade=${encodeURIComponent(p.grade || "")}`;

  const DIAMETERS = [8, 10, 12, 16, 20, 25, 32];
  const CEM_GRADES = ["Grade 43", "Grade 53"];
  const visible = products.filter((p) => {
    if (active === "tmt" && dia) return (p.diameters || []).includes(dia);
    if (active === "cement" && cemGrade) return (p.grade || "").includes(cemGrade.replace("Grade ", ""));
    return true;
  });

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]">
      <Toaster position="bottom-right" theme="dark" richColors />
      <StoreHeader />
      <MetalTicker />
      <PriceFlash />
      <MonthlyBulletinButton />

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 pt-16 pb-10">
        <span className="overline text-[#B87333]">/ The Store</span>
        <h1 className="font-serif text-4xl md:text-7xl mt-4 tracking-[-0.02em]">Materials, priced & specified.</h1>
        <p className="font-body text-[#8A8A82] mt-5 max-w-xl leading-relaxed">
          The master B2B construction catalogue — {totalSkus || "150+"} SKUs across nine project phases, from geosynthetics and smart-road assets to mill-direct metals, polymers and bitumen. Log in as a partner to unlock tier pricing, or raise an RFQ on any line.
        </p>
      </section>

      <PhaseRail phases={phases} active={phase} onSelect={selectPhase} total={totalSkus} />

      {current && (
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pb-6" data-testid="phase-banner">
          <div className="flex items-start gap-4 border-l-2 border-[#B87333] pl-5">
            <PhaseIcon name={current.icon} size={28} className="text-[#B87333] mt-1 shrink-0" />
            <div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333]">Phase {current.phase}</div>
              <h2 className="font-serif text-2xl md:text-3xl mt-1">{current.label}</h2>
              <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">{current.tagline}</p>
            </div>
          </div>
        </div>
      )}

      <div className="sticky top-20 z-30 bg-[#070707]/90 backdrop-blur-md border-y border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4 flex gap-2 overflow-x-auto">
          {[{ key: "all", label: phase ? "All in phase" : "All Products" }, ...chips].map((c) => (
            <button
              key={c.key}
              data-testid={`filter-${c.key}`}
              onClick={() => setActive(c.key)}
              className={`whitespace-nowrap rounded-full px-5 py-2.5 font-grotesk text-xs uppercase tracking-[0.18em] transition-colors ${active === c.key ? "bg-[#F5F5F0] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {(active === "tmt" || active === "cement") && (
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-6" data-testid="spec-filter">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="overline text-[#8A8A82]">
              {active === "tmt" ? "Diameter" : "Grade"}
            </span>
            {active === "tmt" ? (
              <>
                <button
                  data-testid="spec-dia-all"
                  onClick={() => setDia(null)}
                  className={`rounded-full px-4 py-2 font-grotesk text-[11px] uppercase tracking-[0.16em] transition-colors ${dia === null ? "bg-[#B87333] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}
                >
                  All sizes
                </button>
                {DIAMETERS.map((d) => (
                  <button
                    key={d}
                    data-testid={`spec-dia-${d}`}
                    onClick={() => setDia(d)}
                    className={`rounded-full px-4 py-2 font-grotesk text-[11px] uppercase tracking-[0.16em] transition-colors ${dia === d ? "bg-[#B87333] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}
                  >
                    {d} mm
                  </button>
                ))}
              </>
            ) : (
              <>
                <button
                  data-testid="spec-grade-all"
                  onClick={() => setCemGrade(null)}
                  className={`rounded-full px-4 py-2 font-grotesk text-[11px] uppercase tracking-[0.16em] transition-colors ${cemGrade === null ? "bg-[#B87333] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}
                >
                  All grades
                </button>
                {CEM_GRADES.map((g) => (
                  <button
                    key={g}
                    data-testid={`spec-grade-${g.replace("Grade ", "")}`}
                    onClick={() => setCemGrade(g)}
                    className={`rounded-full px-4 py-2 font-grotesk text-[11px] uppercase tracking-[0.16em] transition-colors ${cemGrade === g ? "bg-[#B87333] text-black" : "border border-white/15 text-[#C9C9C2] hover:border-[#B87333]"}`}
                  >
                    {g}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-12">
        {loading ? (
          <p className="font-body text-[#8A8A82]">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="font-body text-[#8A8A82]" data-testid="no-products">No products match this specification.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="product-grid">
            {visible.map((p, i) => (
              <motion.article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className="group rounded-sm border border-white/10 overflow-hidden hover:border-white/25 transition-colors flex flex-col"
              >
                <Link to={`/shop/${p.id}`} className="block relative aspect-[4/3] overflow-hidden">
                  <img src={resolveImg(p.image)} alt={p.name} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${p.in_stock === false ? "grayscale opacity-50" : ""}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute top-3 left-3 font-grotesk text-[10px] uppercase tracking-[0.18em] bg-black/60 backdrop-blur px-3 py-1 rounded-full border border-white/10">{p.grade}</span>
                  {p.in_stock === false && (
                    <span data-testid={`oos-${p.id}`} className="absolute top-3 right-3 font-grotesk text-[10px] uppercase tracking-[0.18em] bg-[#C0392B] text-white px-3 py-1 rounded-full">Out of stock</span>
                  )}
                </Link>
                <div className="p-5 flex flex-col flex-1">
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333]">{p.brand}</div>
                  <Link to={`/shop/${p.id}`}><h3 className="font-serif text-xl mt-1 leading-tight hover:text-[#B87333] transition-colors">{p.name}</h3></Link>
                  <div className="mt-auto pt-4 flex items-end justify-between gap-3">
                    <div>
                      {p.price_pending || !p.price ? (
                        <div className="font-serif text-xl text-[#C9C9C2]" data-testid={`price-pending-${p.id}`}>Price on request</div>
                      ) : (
                        <div className="font-serif text-2xl">{formatINR(p.price)}</div>
                      )}
                      <div className="font-body text-xs text-[#8A8A82]">{p.unit}{p.hsn ? ` · HSN ${p.hsn}` : ""}</div>
                      {p.price_low > 0 && !p.price_pending && (
                        <div className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-[#B87333] mt-1" data-testid={`price-range-${p.id}`}>Range ₹{p.price_low.toLocaleString("en-IN")}–₹{p.price_high.toLocaleString("en-IN")}</div>
                      )}
                    </div>
                    {p.price_pending || !p.price ? (
                      <Link data-testid={`rfq-${p.id}`} to={rfqLink(p)} className="inline-flex items-center gap-2 rounded-full border border-[#B87333] text-[#B87333] px-4 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.18em] hover:bg-[#B87333] hover:text-black transition-colors whitespace-nowrap">
                        <FileText size={13} /> Raise RFQ
                      </Link>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Link data-testid={`rfq-${p.id}`} to={rfqLink(p)} title="Request a quote" className="grid place-items-center w-11 h-11 rounded-full border border-[#B87333]/60 text-[#B87333] hover:bg-[#B87333] hover:text-black transition-colors">
                          <FileText size={16} />
                        </Link>
                        <button data-testid={`add-${p.id}`} disabled={p.in_stock === false} onClick={() => quickAdd(p)} className="grid place-items-center w-11 h-11 rounded-full bg-[#F5F5F0] text-black hover:bg-[#B87333] transition-colors disabled:opacity-30 disabled:hover:bg-[#F5F5F0]">
                          <Plus size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
